import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDataProvider, useNotify } from 'react-admin';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface BrandServiceTemplateFormData {
  name: string;
  description?: string;
  basePrice?: number;
  estimatedDuration?: number;
  taxable: boolean;
  isActive: boolean;
}

interface BrandServiceTemplateFormProps {
  isCreate?: boolean;
}

export const BrandServiceTemplateForm: React.FC<BrandServiceTemplateFormProps> = ({
  isCreate = true,
}) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const brandId = searchParams.get('brandId');
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState<BrandServiceTemplateFormData>({
    name: '',
    description: '',
    basePrice: undefined,
    estimatedDuration: undefined,
    taxable: true,
    isActive: true,
  });

  // Load data if editing
  React.useEffect(() => {
    if (!isCreate && id) {
      const fetchData = async () => {
        try {
          const response = await dataProvider.getOne('brand-service-templates', { id });
          setData(response.data);
        } catch (error) {
          console.error('Error loading template:', error);
          notify('Failed to load service template', { type: 'error' });
        }
      };
      fetchData();
    }
  }, [isCreate, id, dataProvider, notify]);

  const handleChange = (field: keyof BrandServiceTemplateFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value: any;

    if (event.target.type === 'checkbox') {
      value = event.target.checked;
    } else if (field === 'basePrice' || field === 'estimatedDuration') {
      // Convert number fields to numbers, or undefined if empty
      value = event.target.value ? parseFloat(event.target.value) : undefined;
    } else {
      value = event.target.value;
    }

    setData({
      ...data,
      [field]: value,
    });
  };

  const handleSubmit = async () => {
    if (!data.name.trim()) {
      notify('Template name is required', { type: 'warning' });
      return;
    }

    setLoading(true);
    try {
      if (isCreate) {
        if (!brandId) {
          notify('Brand ID is required', { type: 'error' });
          setLoading(false);
          return;
        }
        await dataProvider.create('brand-service-templates', {
          data: {
            ...data,
            brandId,
          },
        });
        notify('Service template created successfully', { type: 'success' });
      } else if (id) {
        await dataProvider.update('brand-service-templates', {
          id,
          data,
        });
        notify('Service template updated successfully', { type: 'success' });
      }
      navigate('/configuration/brand-service-templates');
    } catch (error) {
      console.error('Error saving template:', error);
      notify('Failed to save service template', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/configuration/brand-service-templates')}
          variant="outlined"
        >
          Back
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {isCreate ? 'Create Service Template' : 'Edit Service Template'}
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Template Name"
              value={data.name}
              onChange={handleChange('name')}
              fullWidth
              required
              disabled={loading}
              placeholder="e.g., Standard Cleaning"
            />

            <TextField
              label="Description"
              value={data.description || ''}
              onChange={handleChange('description')}
              fullWidth
              multiline
              rows={3}
              disabled={loading}
              placeholder="Describe this service template"
            />

            <TextField
              label="Base Price"
              type="number"
              value={data.basePrice || ''}
              onChange={handleChange('basePrice')}
              fullWidth
              disabled={loading}
              inputProps={{ step: '0.01', min: '0' }}
              placeholder="0.00"
            />

            <TextField
              label="Estimated Duration (minutes)"
              type="number"
              value={data.estimatedDuration || ''}
              onChange={handleChange('estimatedDuration')}
              fullWidth
              disabled={loading}
              inputProps={{ step: '1', min: '0' }}
              placeholder="60"
            />

            <Box sx={{ display: 'flex', gap: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={data.taxable}
                    onChange={handleChange('taxable')}
                    disabled={loading}
                  />
                }
                label="Taxable"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={data.isActive}
                    onChange={handleChange('isActive')}
                    disabled={loading}
                  />
                }
                label="Active"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : (isCreate ? 'Create' : 'Update')}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/configuration/brand-service-templates')}
                disabled={loading}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export const BrandServiceTemplateCreate = () => <BrandServiceTemplateForm isCreate={true} />;
export const BrandServiceTemplateEdit = () => <BrandServiceTemplateForm isCreate={false} />;
